const STORAGE_KEYS = {
  PLAYER_STATS: 'gato_retro_player_stats',
  PLAYER_HISTORY: 'gato_retro_player_history',
  PLAYER_PREFS: 'gato_retro_player_prefs',
  LAST_PLAYER: 'gato_retro_last_player',
  ACTIVE_SESSION: 'gato_retro_active_session',
  SCHEMA_VERSION: 'gato_retro_schema_v'
} as const;

// Incrementa este número cada vez que cambie la estructura de datos guardados
const CURRENT_SCHEMA_VERSION = 2;

export interface LocalPlayerStats {
  wins: number;
  losses: number;
  ties: number;
  totalGames: number;
  winRate: number;
  currentStreak: number;
  bestStreak: number;
  lastPlayed: string | null;
}

export interface LocalGameRecord {
  id: string;
  opponentName: string;
  playerSymbol: 'X' | 'O';
  playerScore: number;
  opponentScore: number;
  result: 'win' | 'loss' | 'tie';
  date: string;
  duration: number;
}

export interface PlayerPreferences {
  soundEnabled: boolean;
  animationsEnabled: boolean;
  autoConnect: boolean;
  rememberName: boolean;
}

const DEFAULT_STATS: LocalPlayerStats = {
  wins: 0,
  losses: 0,
  ties: 0,
  totalGames: 0,
  winRate: 0,
  currentStreak: 0,
  bestStreak: 0,
  lastPlayed: null
};

const DEFAULT_PREFS: PlayerPreferences = {
  soundEnabled: true,
  animationsEnabled: true,
  autoConnect: true,
  rememberName: true
};

export class LocalStorageService {
  /**
   * Comprueba si el schema de localStorage es compatible con la versión actual.
   * Si no lo es, borra todos los datos y actualiza la versión.
   * Llamar UNA vez al inicio de la app (ej. en App.tsx useEffect / index.tsx).
   */
  static migrateIfNeeded(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SCHEMA_VERSION);
      const storedVersion = stored ? parseInt(stored, 10) : 0;
      if (storedVersion < CURRENT_SCHEMA_VERSION) {
        console.info(
          `[LocalStorage] Schema v${storedVersion} → v${CURRENT_SCHEMA_VERSION}. Limpiando datos obsoletos.`
        );
        this.clearAllData();
        localStorage.setItem(STORAGE_KEYS.SCHEMA_VERSION, String(CURRENT_SCHEMA_VERSION));
      }
    } catch {
      // Si falla la migración, no bloqueamos la app
    }
  }

  static getActiveSession(): { sessionId: string; playerName?: string; playerToken?: string | null } | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_SESSION);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  static setActiveSession(sessionId: string, playerName: string, playerToken: string): void {
    localStorage.setItem(
      STORAGE_KEYS.ACTIVE_SESSION,
      JSON.stringify({ sessionId, playerName, playerToken })
    );
  }

  static clearActiveSession(): void {
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_SESSION);
  }

  static getPlayerName(): string | null {
    return localStorage.getItem(STORAGE_KEYS.LAST_PLAYER);
  }

  static setPlayerName(name: string): void {
    localStorage.setItem(STORAGE_KEYS.LAST_PLAYER, name);
  }

  static clearPlayerName(): void {
    localStorage.removeItem(STORAGE_KEYS.LAST_PLAYER);
  }

  static getPlayerStats(): LocalPlayerStats {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PLAYER_STATS);
      if (!stored) return { ...DEFAULT_STATS };
      return JSON.parse(stored);
    } catch {
      return { ...DEFAULT_STATS };
    }
  }

  static saveGameResult(
    opponentName: string,
    playerSymbol: 'X' | 'O',
    playerScore: number,
    opponentScore: number,
    result: 'win' | 'loss' | 'tie',
    duration: number
  ): LocalGameRecord {
    const stats = this.getPlayerStats();
    const history = this.getPlayerHistory();

    const record: LocalGameRecord = {
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      opponentName,
      playerSymbol,
      playerScore,
      opponentScore,
      result,
      date: new Date().toISOString(),
      duration
    };

    if (result === 'win') {
      stats.wins++;
      stats.currentStreak++;
      if (stats.currentStreak > stats.bestStreak) {
        stats.bestStreak = stats.currentStreak;
      }
    } else if (result === 'loss') {
      stats.losses++;
      stats.currentStreak = 0;
    } else {
      stats.ties++;
    }

    stats.totalGames = stats.wins + stats.losses + stats.ties;
    stats.winRate = stats.totalGames > 0
      ? Math.round((stats.wins / stats.totalGames) * 100)
      : 0;
    stats.lastPlayed = new Date().toISOString();

    localStorage.setItem(STORAGE_KEYS.PLAYER_STATS, JSON.stringify(stats));

    const newHistory = [record, ...history].slice(0, 50);
    localStorage.setItem(STORAGE_KEYS.PLAYER_HISTORY, JSON.stringify(newHistory));

    return record;
  }

  static getPlayerHistory(): LocalGameRecord[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PLAYER_HISTORY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  static getPlayerPreferences(): PlayerPreferences {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PLAYER_PREFS);
      if (!stored) return { ...DEFAULT_PREFS };
      return { ...DEFAULT_PREFS, ...JSON.parse(stored) };
    } catch {
      return { ...DEFAULT_PREFS };
    }
  }

  static savePlayerPreferences(prefs: Partial<PlayerPreferences>): PlayerPreferences {
    const current = this.getPlayerPreferences();
    const updated = { ...current, ...prefs };
    localStorage.setItem(STORAGE_KEYS.PLAYER_PREFS, JSON.stringify(updated));
    return updated;
  }

  static clearAllData(): void {
    localStorage.removeItem(STORAGE_KEYS.PLAYER_STATS);
    localStorage.removeItem(STORAGE_KEYS.PLAYER_HISTORY);
    localStorage.removeItem(STORAGE_KEYS.PLAYER_PREFS);
    localStorage.removeItem(STORAGE_KEYS.LAST_PLAYER);
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_SESSION);
  }

  static exportData(): string {
    const data = {
      stats: this.getPlayerStats(),
      history: this.getPlayerHistory(),
      prefs: this.getPlayerPreferences(),
      playerName: this.getPlayerName(),
      exportedAt: new Date().toISOString()
    };
    return JSON.stringify(data, null, 2);
  }

  static importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      if (data.stats) {
        localStorage.setItem(STORAGE_KEYS.PLAYER_STATS, JSON.stringify(data.stats));
      }
      if (data.history) {
        localStorage.setItem(STORAGE_KEYS.PLAYER_HISTORY, JSON.stringify(data.history));
      }
      if (data.prefs) {
        localStorage.setItem(STORAGE_KEYS.PLAYER_PREFS, JSON.stringify(data.prefs));
      }
      if (data.playerName) {
        localStorage.setItem(STORAGE_KEYS.LAST_PLAYER, data.playerName);
      }
      if (data.activeSession) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSION, JSON.stringify(data.activeSession));
      }
      return true;
    } catch {
      return false;
    }
  }

  static getWinRate(): number {
    const stats = this.getPlayerStats();
    return stats.winRate;
  }

  static getRecentForm(limit: number = 10): string[] {
    const history = this.getPlayerHistory();
    return history.slice(0, limit).map(r => r.result);
  }
}

export default LocalStorageService;
